import {
  ArrowUUpLeftIcon,
  CalendarDotsIcon,
  CheckCircleIcon,
  CircleIcon,
  LightningIcon,
  PencilSimpleIcon,
  PlusIcon,
  ProhibitIcon,
  SealCheckIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import {
  type PlainDate,
  billedInstallmentsCount,
  formatMoney,
  todayIn,
} from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Card, EmptyState } from '@/components/Card'
import { Donut, type DonutSegment, ProgressRing, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { AvisoError, Campo, Monto } from '@/components/Form'
import { HojaInferior } from '@/components/HojaInferior'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { CreditCard, InstallmentPlan, ScheduledInstallment } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useCalendarioMeses,
  useCancelarCompraMeses,
  useCards,
  useLiquidarCompraMeses,
  useMeses,
} from '@/lib/queries'
import { useSession } from '@/lib/session'
import { mensajeDeError } from '@/routes/Recurring'

/**
 * Compras a meses (MSI/MCI) del hogar.
 *
 * QUÉ PREGUNTA CONTESTA
 *
 * «¿Cuánto se me va cada mes en cosas que ya compré?». Por eso la pantalla abre
 * con esa cifra y no con la lista: las mensualidades son la parte del gasto
 * sobre la que **ya no se puede decidir** —el refrigerador está en la cocina y
 * las nueve mensualidades que faltan van a caer pase lo que pase—, y verla
 * primero es lo que permite planear el mes con la cifra correcta.
 *
 * La lista va después y ordenada por lo que queda por pagar, de mayor a menor:
 * la compra que más pesa en los meses que vienen es la que hay que reconocer
 * primero, no la más reciente.
 *
 * POR QUÉ NO HAY BARRAS
 *
 * El reparto por tarjeta es una dona. Lo que se pregunta es «de este total, qué
 * parte sale de cada tarjeta», y eso es una proporción de un todo cerrado; una
 * barra obligaría a comparar longitudes contra un origen para responder lo
 * mismo.
 */

export function Meses() {
  const navigate = useNavigate()
  // La tarjeta puede venir en la URL: es el enlace directo desde la pantalla de
  // Tarjetas, que entra aquí preguntando por las compras de una sola.
  const [params, setParams] = useSearchParams()
  const tarjetaFiltro = params.get('tarjeta') ?? ''
  const [verCerradas, setVerCerradas] = useState(false)
  const [abierto, setAbierto] = useState<string | null>(null)

  const tarjetas = useCards()
  const { data, isPending, isError, error, refetch } = useMeses()

  const planes = data?.plans ?? []
  const activos = useMemo(() => planes.filter((p) => p.status === 'ACTIVE'), [planes])
  const cerrados = useMemo(() => planes.filter((p) => p.status !== 'ACTIVE'), [planes])

  /**
   * Lo que se va cada mes, por tarjeta.
   *
   * La regla es la del servidor y no una nueva: **la siguiente** mensualidad de
   * cada plan vivo, nunca la suma de todas ni el precio de la compra. Se agrupa
   * aquí en vez de leer `summary.monthlyCommittedCents` porque la dona necesita
   * que las partes sumen exactamente el centro; con dos orígenes distintos, un
   * plan sin próxima mensualidad haría que el todo no cuadrara con sus tramos.
   */
  const porTarjeta = useMemo(() => {
    const mapa = new Map<string, { nombre: string; last4: string | null; cents: number }>()
    for (const plan of activos) {
      const previo = mapa.get(plan.creditCardId)
      const cents = (previo?.cents ?? 0) + (plan.nextInstallment?.amountCents ?? 0)
      mapa.set(plan.creditCardId, { nombre: plan.card.name, last4: plan.card.last4, cents })
    }
    return [...mapa.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .filter((t) => t.cents > 0)
      .sort((a, b) => b.cents - a.cents)
  }, [activos])

  const mensualCents = porTarjeta.reduce((s, t) => s + t.cents, 0)

  if (isPending) return <Cargando />

  if (isError || !data) {
    return (
      <Pagina>
        <Encabezado subtitulo="" onAnadir={() => navigate('/tarjetas/meses/nueva')} />
        <ErrorState
          titulo="No pudimos cargar tus compras a meses"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      </Pagina>
    )
  }

  const sinTarjetas = (tarjetas.data ?? []).filter((c) => !c.archivedAt).length === 0

  if (planes.length === 0) {
    return (
      <Pagina>
        <Encabezado subtitulo="Sin compras a meses" onAnadir={() => navigate('/tarjetas/meses/nueva')} />
        <div className="px-safe mx-auto max-w-lg pt-1">
          <Card>
            <EmptyState
              icon={<CalendarDotsIcon size={32} weight="fill" />}
              title="Aquí van tus compras a meses"
              description={
                sinTarjetas
                  ? 'Una compra a meses es la que pagas en 3, 6 o 12 mensualidades con tu tarjeta. Para registrar la primera necesitas dar de alta la tarjeta con la que compraste.'
                  : 'Es esa compra que difieres a 3, 6 o 12 mensualidades con la tarjeta. Regístrala y PayBae la reparte en los cortes que vienen: dejas de ver el precio completo y empiezas a ver lo que de verdad te cobran cada mes, ya descontado de tu disponible.'
              }
              action={
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    navigate(sinTarjetas ? '/tarjetas/nueva' : '/tarjetas/meses/nueva')
                  }}
                  className="flex min-h-[44px] items-center gap-2 rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                >
                  <PlusIcon size={16} weight="bold" />
                  {sinTarjetas ? 'Añadir una tarjeta' : 'Registrar una compra a meses'}
                </button>
              }
            />
          </Card>
        </div>
      </Pagina>
    )
  }

  const visibles = (verCerradas ? planes : activos)
    .filter((p) => tarjetaFiltro === '' || p.creditCardId === tarjetaFiltro)
    // De mayor a menor por lo que queda por pagar: la compra que más pesa en los
    // meses que vienen manda. Los planes cerrados ya no deben nada, así que caen
    // solos al final y ahí se ordenan por cuándo se detuvieron.
    .sort(
      (a, b) =>
        b.remainingBalanceCents - a.remainingBalanceCents ||
        (b.stoppedOn ?? '').localeCompare(a.stoppedOn ?? ''),
    )

  // Cuándo termina de pagarse la última compra viva. Las fechas son
  // `AAAA-MM-DD`, así que comparar como cadenas ordena igual que comparar como
  // fechas y no hace falta construir un `Date` —que además correría el día.
  let ultima: string | undefined
  for (const plan of activos) {
    const fin = plan.lastInstallment?.dueDate
    if (fin !== undefined && (ultima === undefined || fin > ultima)) ultima = fin
  }

  const planAbierto = planes.find((p) => p.id === abierto) ?? null
  const tarjetaDelPlan =
    (tarjetas.data ?? []).find((c) => c.id === planAbierto?.creditCardId) ?? null

  return (
    <Pagina>
      <Encabezado
        // Corto a propósito: en 375 px el subtítulo de una sola línea se trunca
        // enseguida, y «la última termina en septiembre de 2027» se quedaba en
        // «termina en s…», que no dice nada.
        subtitulo={
          activos.length === 0
            ? 'Ninguna activa'
            : `${activos.length} ${activos.length === 1 ? 'activa' : 'activas'}${
                ultima ? ` · terminas en ${mesYAno(ultima)}` : ''
              }`
        }
        onAnadir={() => navigate('/tarjetas/meses/nueva')}
      />

      <div className="px-safe mx-auto max-w-lg">
        {/* ── Apertura: lo que ya no se puede dejar de gastar ────────────────
            Va antes que la lista y en cuerpo de titular. Es la única cifra de
            esta pantalla con consecuencia sobre el mes que viene. En tinta,
            nunca en verde ni en coral: es un hecho, no una alarma. */}
        <section className="pb-6 pt-3">
          <p className="mm-versalita text-ink-soft">Se te va cada mes en mensualidades</p>
          <p data-money className="mm-titular mt-1.5">
            {partirMonto(mensualCents).entero}
            {/* Los centavos en cuerpo pequeño sobre la misma línea de base, como
                en el inicio: el peso visual va a los pesos, que es lo que se
                compara contra el sueldo. */}
            <span className="mm-centavos">{partirMonto(mensualCents).centavos}</span>
          </p>
          <p className="mt-2.5 max-w-[22rem] text-[13px] leading-snug text-ink-soft">
            {mensualCents === 0
              ? 'Ninguna de tus compras a meses cobra en los cortes que vienen.'
              : 'Ya está comprometido: son compras hechas, y esa parte del mes no admite decisión.'}
          </p>
        </section>

        {porTarjeta.length > 1 ? (
          <Seccion numero="01" titulo="De qué tarjeta sale">
            <div className="flex items-center gap-4">
              <Donut
                segments={porTarjeta.map(
                  (t, i): DonutSegment => ({
                    key: t.id,
                    label: t.nombre,
                    value: t.cents,
                    color: chartColor(i),
                  }),
                )}
                centerValue={formatMoney(mensualCents, { hideZeroCents: true })}
                centerLabel="al mes"
              />
              <ul className="flex min-w-0 flex-1 flex-col gap-2.5">
                {porTarjeta.map((t, i) => (
                  <li key={t.id} className="flex items-baseline gap-2">
                    <span
                      aria-hidden
                      className="size-2 shrink-0 translate-y-[-1px] rounded-full"
                      style={{ background: chartColor(i) }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] leading-tight text-ink-soft">
                      {t.nombre}
                      {t.last4 ? <span className="text-ink-faint"> ·· {t.last4}</span> : null}
                    </span>
                    <span data-money className="shrink-0 text-[13px] font-bold text-ink">
                      {formatMoney(t.cents, { hideZeroCents: true })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Seccion>
        ) : null}

        <Seccion numero={porTarjeta.length > 1 ? '02' : '01'} titulo="Tus compras">
          {/* Filtros. La tarjeta viaja en la URL para que el enlace desde una
              tarjeta concreta abra ya filtrado y se pueda compartir. */}
          {porTarjeta.length > 1 || tarjetaFiltro !== '' ? (
            <div className="mb-3 flex flex-wrap gap-2">
              <Pildora
                activa={tarjetaFiltro === ''}
                onClick={() => setParams({}, { replace: true })}
              >
                Todas
              </Pildora>
              {(tarjetas.data ?? [])
                .filter((c) => planes.some((p) => p.creditCardId === c.id))
                .map((c) => (
                  <Pildora
                    key={c.id}
                    activa={tarjetaFiltro === c.id}
                    onClick={() => setParams({ tarjeta: c.id }, { replace: true })}
                  >
                    {c.name}
                    {c.last4 ? <span className="font-mono font-normal"> ·{c.last4}</span> : null}
                  </Pildora>
                ))}
            </div>
          ) : null}

          {cerrados.length > 0 ? (
            <div className="mb-1">
              <Pildora
                activa={verCerradas}
                onClick={() => setVerCerradas((v) => !v)}
                icono={<SealCheckIcon size={13} weight="fill" />}
              >
                {/* La etiqueta dice lo que hace el siguiente toque, no el estado
                    en el que está: encendido y con el mismo texto, «Ver» parecía
                    un botón que no funcionó. */}
                {verCerradas ? 'Ocultar liquidadas y canceladas' : 'Ver liquidadas y canceladas'}
                <span className="font-mono font-normal"> ({cerrados.length})</span>
              </Pildora>
            </div>
          ) : null}

          {visibles.length === 0 ? (
            <p className="py-8 text-[13px] leading-snug text-ink-soft">
              {tarjetaFiltro === ''
                ? 'No te queda ninguna compra a meses por pagar. Cuando difieras una nueva, aparecerá aquí.'
                : 'Esta tarjeta no tiene compras a meses activas. Toca «Todas» para ver las del resto.'}
            </p>
          ) : (
            <ul className="mm-filas">
              {visibles.map((plan) => (
                <FilaPlan
                  key={plan.id}
                  plan={plan}
                  onAbrir={() => {
                    haptic('light')
                    setAbierto(plan.id)
                  }}
                />
              ))}
            </ul>
          )}
        </Seccion>

        {/* Colofón: recuerda en una línea la regla que hace que las fechas de
            arriba sean las que son. Dos líneas de monoespaciada en versalitas
            pesan demasiado para un pie. */}
        <p className="mm-mono mt-8 border-t border-line pt-4">
          Cada mensualidad cae en su propio corte
        </p>
      </div>

      <HojaPlan
        planId={abierto}
        tarjeta={tarjetaDelPlan}
        onCerrar={() => setAbierto(null)}
        onEditar={(id) => navigate(`/tarjetas/meses/${id}/editar`)}
      />
    </Pagina>
  )
}

/* ── Piezas de la lista ─────────────────────────────────────────────────── */

function FilaPlan({ plan, onAbrir }: { plan: InstallmentPlan; onAbrir: () => void }) {
  const activo = plan.status === 'ACTIVE'
  // Un plan liquidado ya no debe nada, así que el anillo se cierra aunque
  // `paidCount` se haya quedado en las mensualidades que la tarjeta alcanzó a
  // cobrarle. Uno cancelado no se cierra: ahí el anillo tiene que decir hasta
  // dónde llegó, que es justo lo que se pagó de verdad.
  const pct = plan.status === 'SETTLED' ? 100 : plan.progressPct
  const color = activo
    ? 'var(--primary)'
    : plan.status === 'SETTLED'
      ? 'var(--income)'
      : 'var(--ink-faint)'

  return (
    <li>
      <button
        type="button"
        onClick={onAbrir}
        className="flex min-h-[44px] w-full items-center gap-3 py-3 text-left active:opacity-70"
      >
        <ProgressRing percent={pct} size={48} stroke={5} color={color}>
          {plan.status === 'SETTLED' ? (
            <CheckCircleIcon size={17} weight="fill" className="text-income-text" />
          ) : (
            <span className="font-mono text-[10px] font-bold text-ink">
              {plan.paidCount}/{plan.months}
            </span>
          )}
        </ProgressRing>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[14px] font-semibold text-ink">
            <span className="truncate">{plan.name}</span>
            {/* Solo se marca la compra CON intereses, no la que no los tiene.
                A meses sin intereses es lo normal aquí; lo que hay que poder
                reconocer de un vistazo en una lista es la que cuesta de más.
                Etiquetar las dos gastaría tinta en repetir lo esperado. */}
            {!plan.interestFree ? (
              <span className="shrink-0 rounded-pill bg-expense-soft px-1.5 py-px text-[10px] font-bold uppercase tracking-[0.06em] text-expense-text">
                MCI
              </span>
            ) : null}
          </p>
          {/* El plazo no se repite aquí: ya lo dice el anillo —«3/12»— y en una
              línea de 375 px con un nombre de tarjeta largo, repetirlo era justo
              lo que se truncaba. */}
          <p className="truncate text-[12px] text-ink-soft">
            {plan.card.name}
            {plan.card.last4 ? <span className="font-mono"> ·· {plan.card.last4}</span> : null}
          </p>
          {!activo ? (
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
              {plan.status === 'SETTLED' ? 'Liquidada' : 'Cancelada'}
              {plan.stoppedOn ? ` · ${formatoCorto(plan.stoppedOn)}` : ''}
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          <p data-money className="text-[14px] font-bold text-ink">
            {formatMoney(plan.monthlyAmountCents, { hideZeroCents: true })}
            <span className="text-[11px] font-medium text-ink-faint">/mes</span>
          </p>
          <p data-money className="mt-0.5 text-[12px] text-ink-soft">
            {activo
              ? `faltan ${formatMoney(plan.remainingBalanceCents, { hideZeroCents: true })}`
              : 'no debes nada'}
          </p>
        </div>
      </button>
    </li>
  )
}

/* ── Hoja de detalle: el calendario completo ────────────────────────────── */

type Confirmacion = 'ninguna' | 'liquidar' | 'cancelar'

/**
 * Detalle de una compra a meses.
 *
 * Es «todo lo demás» de la pregunta: las N mensualidades con su corte, su fecha
 * límite y si ya se pagaron. Va en hoja inferior y no en pantalla propia porque
 * se consulta de vuelta a la lista —se abre una, se cierra, se abre otra— y una
 * navegación completa por cada vistazo perdería el sitio cada vez.
 */
function HojaPlan({
  planId,
  tarjeta,
  onCerrar,
  onEditar,
}: {
  planId: string | null
  tarjeta: CreditCard | null
  onCerrar: () => void
  onEditar: (id: string) => void
}) {
  const { data, isPending, isError, error, refetch } = useCalendarioMeses(planId)
  const liquidar = useLiquidarCompraMeses()
  const cancelar = useCancelarCompraMeses()

  const [confirmando, setConfirmando] = useState<Confirmacion>('ninguna')
  /** `null` = lo que diga el servidor. Con número, lo que el banco cobra. */
  const [montoLiquidacion, setMontoLiquidacion] = useState<number | null>(null)

  const cerrar = (): void => {
    setConfirmando('ninguna')
    setMontoLiquidacion(null)
    liquidar.reset()
    cancelar.reset()
    onCerrar()
  }

  const plan = data?.plan ?? null

  /**
   * Lo que la liquidación cobraría de golpe.
   *
   * La frontera es **el corte abierto**, no la marca de pagada: una mensualidad
   * de un ciclo que ya cortó la cobra ese estado de cuenta aunque todavía no
   * haya vencido, y adelantarla otra vez la cobraría dos veces. Es la misma
   * cuenta que hace el servidor al liquidar, así que el número que se anuncia
   * aquí es el que se va a cobrar.
   */
  const pendienteCents = useMemo(() => {
    const corte = tarjeta?.openStatement?.cutoffDate
    if (!data || !corte) return null
    return data.installments
      .filter((i) => i.cutoffDate >= corte)
      .reduce((s, i) => s + i.amountCents, 0)
  }, [data, tarjeta])

  return (
    <HojaInferior
      abierta={planId !== null}
      titulo={plan?.name ?? 'Compra a meses'}
      descripcion={
        plan
          ? `${plan.card.name}${plan.card.last4 ? ` ·· ${plan.card.last4}` : ''} · ${
              plan.interestFree
                ? `${plan.months} meses sin intereses`
                : `${plan.months} meses con intereses`
            }`
          : undefined
      }
      onCerrar={cerrar}
    >
      {isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-[92px]" />
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[44px]" />
          ))}
        </div>
      ) : isError || !plan || !data ? (
        <ErrorState
          titulo="No pudimos abrir esta compra"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      ) : (
        <>
          <Resumen plan={plan} />
          <AvisoMensualidadesPagadas plan={plan} tarjeta={tarjeta} onEditar={onEditar} />

          <h3 className="mm-versalita mt-4 px-1 text-ink-soft">Calendario de mensualidades</h3>
          <ul className="mt-1 flex flex-col">
            {data.installments.map((cuota) => (
              <FilaCuota
                key={cuota.number}
                cuota={cuota}
                esLaQueSigue={cuota.number === plan.nextInstallment?.number}
              />
            ))}
          </ul>

          {plan.status === 'ACTIVE' ? (
            confirmando === 'ninguna' ? (
              <div className="mt-4 flex flex-col gap-2">
                <AccionHoja
                  Icon={PencilSimpleIcon}
                  label="Editar la compra"
                  detalle="Corrige el precio, el plazo o la fecha"
                  onClick={() => onEditar(plan.id)}
                />
                <AccionHoja
                  Icon={LightningIcon}
                  label="Liquidar compra"
                  detalle="Ya la pagaste de golpe: lo que falta se cobra en el próximo corte"
                  onClick={() => setConfirmando('liquidar')}
                />
                <AccionHoja
                  Icon={ProhibitIcon}
                  label="Cancelar la compra"
                  detalle="Deja de cobrar lo que falta"
                  peligro
                  onClick={() => setConfirmando('cancelar')}
                />
              </div>
            ) : confirmando === 'liquidar' ? (
              <ConfirmarLiquidacion
                plan={plan}
                pendienteCents={pendienteCents}
                monto={montoLiquidacion}
                onMonto={setMontoLiquidacion}
                guardando={liquidar.isPending}
                error={liquidar.isError ? mensajeDeError(liquidar.error) : null}
                onVolver={() => setConfirmando('ninguna')}
                onConfirmar={() =>
                  liquidar.mutate(
                    {
                      id: plan.id,
                      // Sin monto no se manda el campo: el servidor cobra lo que
                      // aún no ha cortado, que es su propia cuenta y no una que
                      // el cliente tenga que adivinar.
                      ...(montoLiquidacion !== null ? { amountCents: montoLiquidacion } : {}),
                    },
                    {
                      onSuccess: () => {
                        haptic('success')
                        cerrar()
                      },
                      onError: () => haptic('error'),
                    },
                  )
                }
              />
            ) : (
              <ConfirmarCancelacion
                plan={plan}
                guardando={cancelar.isPending}
                error={cancelar.isError ? mensajeDeError(cancelar.error) : null}
                onVolver={() => setConfirmando('ninguna')}
                onConfirmar={() =>
                  cancelar.mutate(plan.id, {
                    onSuccess: () => {
                      haptic('success')
                      cerrar()
                    },
                    onError: () => haptic('error'),
                  })
                }
              />
            )
          ) : (
            <p className="mt-4 rounded-card bg-surface-sunken px-3.5 py-3 text-[13px] leading-snug text-ink-soft">
              {plan.status === 'SETTLED'
                ? plan.settlementAmountCents !== null
                  ? `La liquidaste el ${formatoLargo(plan.stoppedOn ?? plan.purchasedOn)}: se cobraron ${formatMoney(plan.settlementAmountCents, { hideZeroCents: true })} de golpe en el corte abierto de entonces.`
                  : 'Esta compra ya terminó de pagarse. Se queda aquí como historial.'
                : `La cancelaste el ${formatoLargo(plan.stoppedOn ?? plan.purchasedOn)}. Lo que la tarjeta ya había cobrado se quedó en los cortes que cerraron; lo que faltaba dejó de cobrarse.`}
            </p>
          )}
        </>
      )}
    </HojaInferior>
  )
}

/**
 * Aviso cuando las mensualidades declaradas no cuadran con los cortes.
 *
 * `monthsPaid` lo captura la persona y gobierna todo el calendario del plan:
 * uno de más corre las fechas un mes entero y la siguiente mensualidad se
 * anuncia en el corte que no es. El síntoma es una fecha rara semanas después,
 * y no hay manera de relacionarla con un número que no se ve en ninguna parte.
 *
 * La app puede calcular ese número —fecha de compra más día de corte— así que
 * aquí lo compara y lo dice. No lo corrige sola a propósito: pagar por
 * adelantado o venir atrasado son casos legítimos que solo la persona conoce, y
 * cambiarle un dato de dinero sin preguntar es peor que el error que evita.
 */
function AvisoMensualidadesPagadas({
  plan,
  tarjeta,
  onEditar,
}: {
  plan: InstallmentPlan
  tarjeta: CreditCard | null
  onEditar: (id: string) => void
}) {
  const { membership } = useSession()
  const hoy = todayIn(membership.household.timezone) as PlainDate
  if (plan.status !== 'ACTIVE' || !tarjeta) return null

  const cobradas = billedInstallmentsCount(
    { purchasedOn: plan.purchasedOn, months: plan.months },
    hoy,
    {
      statementDay: tarjeta.statementDay,
      paymentTermDays: tarjeta.paymentTermDays,
      paymentTermType: tarjeta.paymentTermType,
      cutoffInclusive: tarjeta.cutoffInclusive,
    },
  )
  if (cobradas === plan.monthsPaid) return null

  const deMas = plan.monthsPaid > cobradas
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-card bg-warning-soft px-3.5 py-3">
      <p className="flex items-start gap-2 text-[12.5px] leading-snug text-ink">
        <WarningIcon size={15} className="mt-0.5 shrink-0 text-warning" />
        <span>
          Al registrarla dijiste que llevabas <strong>{plan.monthsPaid}</strong>{' '}
          {plan.monthsPaid === 1 ? 'mensualidad pagada' : 'mensualidades pagadas'}, pero desde el{' '}
          {formatoLargo(plan.purchasedOn)} tu {tarjeta.name} solo ha cortado{' '}
          <strong>{cobradas}</strong> {cobradas === 1 ? 'vez' : 'veces'}.{' '}
          {deMas
            ? 'Por eso la siguiente mensualidad aparece un corte más tarde de lo que esperarías.'
            : 'Por eso te estamos cobrando un corte que quizá ya pagaste.'}
        </span>
      </p>
      <button
        type="button"
        onClick={() => {
          haptic('light')
          onEditar(plan.id)
        }}
        className="min-h-[40px] self-start rounded-pill bg-surface px-4 text-[13px] font-semibold text-ink active:scale-95"
      >
        Revisar la compra
      </button>
    </div>
  )
}

/** Las cuatro cifras del plan, con el anillo como ancla. */
function Resumen({ plan }: { plan: InstallmentPlan }) {
  const activo = plan.status === 'ACTIVE'
  return (
    // En superficie y no en el bloque hundido: la pista del anillo es
    // `--surface-sunken`, así que sobre un fondo hundido el anillo perdía la
    // parte no cumplida y el arco quedaba flotando sin círculo.
    <div className="rounded-card border border-line bg-surface p-3.5">
      <div className="flex items-center gap-3.5">
        <ProgressRing
          percent={plan.status === 'SETTLED' ? 100 : plan.progressPct}
          size={62}
          stroke={7}
          color={activo ? 'var(--primary)' : plan.status === 'SETTLED' ? 'var(--income)' : 'var(--ink-faint)'}
        >
          <span className="font-mono text-[12px] font-bold text-ink">
            {plan.paidCount}/{plan.months}
          </span>
        </ProgressRing>
        <div className="min-w-0 flex-1">
          <p className="mm-versalita text-ink-soft">Mensualidad</p>
          <p data-money className="font-display text-[26px] font-extrabold leading-none tracking-[-0.04em] text-ink">
            {formatMoney(plan.monthlyAmountCents)}
          </p>
          <p data-money className="mt-1.5 text-[12px] text-ink-soft">
            {activo
              ? `Te faltan ${plan.remainingCount} de ${plan.months} · ${formatMoney(plan.remainingBalanceCents, { hideZeroCents: true })}`
              : `Pagaste ${plan.paidCount} de ${plan.months} mensualidades`}
          </p>
        </div>
      </div>

      <dl className="mt-3.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line pt-3">
        <Dato etiqueta="Precio de la compra" cents={plan.totalAmountCents} />
        {/* «Vas a pagar en total» y los intereses solo existen en MCI. En una
            compra sin intereses el total es idéntico al precio y los intereses
            son cero: dos casillas que repiten lo de al lado enseñan a no leer
            esta rejilla el día que sí traen número. */}
        {!plan.interestFree ? (
          <>
            <Dato etiqueta="Vas a pagar en total" cents={plan.totalToPayCents} />
            <Dato etiqueta="De eso, intereses" cents={plan.interestCents} tono="expense" />
          </>
        ) : null}
        {plan.category ? (
          <div className="min-w-0">
            <dt className="mm-versalita text-ink-faint">Categoría</dt>
            <dd className="truncate text-[14px] font-semibold text-ink">{plan.category.name}</dd>
          </div>
        ) : null}
      </dl>

      <p className="mt-3 text-[12px] leading-snug text-ink-faint">
        Comprada el {formatoLargo(plan.purchasedOn)}. La registraste el{' '}
        {formatoLargo(plan.bookedOn)}: desde ese corte responde PayBae por ella, y los anteriores
        se quedan como los pagaste.
      </p>
    </div>
  )
}

function Dato({
  etiqueta,
  cents,
  tono,
}: {
  etiqueta: string
  cents: number
  tono?: 'expense'
}) {
  return (
    <div className="min-w-0">
      <dt className="mm-versalita text-ink-faint">{etiqueta}</dt>
      <dd
        data-money
        className={cn(
          'text-[14px] font-semibold',
          tono === 'expense' ? 'text-expense-text' : 'text-ink',
        )}
      >
        {formatMoney(cents, { hideZeroCents: true })}
      </dd>
    </div>
  )
}

/**
 * Una mensualidad del calendario.
 *
 * Dos fechas, no una: el corte decide en qué estado de cuenta cae y el límite
 * es cuándo sale el dinero de verdad. Enseñar solo una de las dos es lo que
 * hace que la gente crea que el cargo del día 20 se paga el día 20.
 */
function FilaCuota({
  cuota,
  esLaQueSigue,
}: {
  cuota: ScheduledInstallment
  esLaQueSigue: boolean
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-card px-3 py-2.5',
        esLaQueSigue && 'bg-primary-soft',
      )}
    >
      <span className={cn('shrink-0', cuota.charged ? 'text-income-text' : 'text-ink-faint')}>
        {cuota.charged ? (
          <CheckCircleIcon size={18} weight="fill" />
        ) : (
          <CircleIcon size={18} weight="bold" />
        )}
      </span>
      <span className="w-[26px] shrink-0 font-mono text-[12px] font-medium text-ink-faint">
        {String(cuota.number).padStart(2, '0')}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">
          Corte {formatoCorto(cuota.cutoffDate)}
          <span className="text-ink-soft"> · pagas {formatoCorto(cuota.dueDate)}</span>
        </span>
        {esLaQueSigue ? (
          <span className="mm-versalita block text-primary-text">La que sigue</span>
        ) : cuota.charged ? (
          <span className="block text-[11px] text-ink-faint">Ya te la cobraron</span>
        ) : null}
      </span>
      <span
        data-money
        className={cn(
          'shrink-0 text-[13px] font-semibold',
          cuota.charged ? 'text-ink-faint' : 'text-ink',
        )}
      >
        {formatMoney(cuota.amountCents, { hideZeroCents: true })}
      </span>
    </li>
  )
}

function AccionHoja({
  Icon,
  label,
  detalle,
  peligro = false,
  onClick,
}: {
  Icon: typeof PencilSimpleIcon
  label: string
  detalle: string
  peligro?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      className="flex min-h-[52px] w-full items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
    >
      <span className={cn('shrink-0', peligro ? 'text-expense-text' : 'text-ink-soft')}>
        <Icon size={19} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[14px] font-semibold', peligro ? 'text-expense-text' : 'text-ink')}>
          {label}
        </span>
        <span className="block text-[12px] leading-snug text-ink-soft">{detalle}</span>
      </span>
    </button>
  )
}

function ConfirmarLiquidacion({
  plan,
  pendienteCents,
  monto,
  onMonto,
  guardando,
  error,
  onVolver,
  onConfirmar,
}: {
  plan: InstallmentPlan
  pendienteCents: number | null
  monto: number | null
  onMonto: (v: number | null) => void
  guardando: boolean
  error: string | null
  onVolver: () => void
  onConfirmar: () => void
}) {
  const excede = monto !== null && pendienteCents !== null && monto > pendienteCents
  return (
    <Confirmacion
      titulo="¿Liquidar por adelantado?"
      cuerpo={
        pendienteCents === null
          ? 'Las mensualidades que aún no cortan dejarán de cobrarse mes a mes y se cobrarán de golpe en tu estado de cuenta abierto. La deuda no desaparece: se adelanta.'
          : `Las mensualidades que aún no cortan —${formatMoney(pendienteCents, { hideZeroCents: true })}— dejan de cobrarse mes a mes y se cobran de golpe en tu estado de cuenta abierto. La deuda no desaparece: se adelanta.`
      }
      etiqueta={
        monto !== null
          ? `Liquidar con ${formatMoney(monto, { hideZeroCents: true })}`
          : 'Sí, liquidar'
      }
      guardando={guardando}
      deshabilitado={excede || (monto !== null && monto <= 0)}
      error={error ?? (excede ? 'No puedes liquidar con más de lo que falta por pagar.' : null)}
      onVolver={onVolver}
      onConfirmar={onConfirmar}
    >
      {/* Solo en MCI. En una compra sin intereses no hay intereses que perdonar,
          así que ofrecer «me cobran otra cantidad» invitaría a inventar un
          descuento que el banco no da. */}
      {!plan.interestFree ? (
        monto === null ? (
          <button
            type="button"
            onClick={() => onMonto(pendienteCents ?? plan.remainingBalanceCents)}
            className="min-h-[44px] text-left text-[13px] font-semibold text-primary-text active:opacity-60"
          >
            El banco me cobra otra cantidad
          </button>
        ) : (
          <Campo
            label="¿Cuánto te cobran por liquidar?"
            ayuda="Al liquidar antes, el banco suele perdonar los intereses que no se han generado. Escribe lo que te dijeron que vas a pagar."
          >
            <Monto cents={monto} onChange={(v) => onMonto(typeof v === 'function' ? v(monto) : v)} />
          </Campo>
        )
      ) : null}
    </Confirmacion>
  )
}

function ConfirmarCancelacion({
  plan,
  guardando,
  error,
  onVolver,
  onConfirmar,
}: {
  plan: InstallmentPlan
  guardando: boolean
  error: string | null
  onVolver: () => void
  onConfirmar: () => void
}) {
  return (
    <Confirmacion
      titulo="¿Cancelar esta compra?"
      cuerpo={`Lo que la tarjeta ya te cobró se queda en los cortes que cerraron: eso lo pagaste de verdad y borrarlo dejaría tus saldos sin cuadrar. Solo dejamos de cobrar ${
        plan.remainingCount === 1
          ? 'la mensualidad que falta'
          : `las ${plan.remainingCount} mensualidades que faltan`
      }. Úsalo si devolviste la compra o si el banco te la canceló.`}
      etiqueta="Sí, cancelar la compra"
      peligro
      guardando={guardando}
      error={error}
      onVolver={onVolver}
      onConfirmar={onConfirmar}
    />
  )
}

/** Panel de confirmación dentro de la propia hoja: nunca se pierde el contexto. */
function Confirmacion({
  titulo,
  cuerpo,
  etiqueta,
  peligro = false,
  guardando,
  deshabilitado = false,
  error,
  children,
  onVolver,
  onConfirmar,
}: {
  titulo: string
  cuerpo: string
  etiqueta: string
  peligro?: boolean
  guardando: boolean
  deshabilitado?: boolean
  error: string | null
  children?: React.ReactNode
  onVolver: () => void
  onConfirmar: () => void
}) {
  return (
    <div className="mt-4 flex flex-col gap-3 rounded-card border border-line bg-surface p-3.5">
      <div>
        <p className="font-display text-[16px] font-bold tracking-[-0.02em] text-ink">{titulo}</p>
        <p className="mt-1 text-[13px] leading-snug text-ink-soft">{cuerpo}</p>
      </div>

      {children}
      <AvisoError mensaje={error} />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onVolver()
          }}
          className="flex min-h-[48px] flex-1 items-center justify-center gap-1.5 rounded-card border border-line bg-surface text-[14px] font-semibold text-ink active:scale-[0.98]"
        >
          <ArrowUUpLeftIcon size={15} weight="bold" />
          Mejor no
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={guardando || deshabilitado}
          className={cn(
            'flex min-h-[48px] flex-[1.4] items-center justify-center rounded-card text-[14px] font-semibold active:scale-[0.98]',
            guardando || deshabilitado
              ? 'cursor-not-allowed bg-surface-sunken text-ink-faint'
              : peligro
                ? 'bg-expense text-white'
                : 'bg-accion text-on-accion',
          )}
        >
          {guardando ? 'Guardando…' : etiqueta}
        </button>
      </div>
    </div>
  )
}

/* ── Lienzo y utilidades ────────────────────────────────────────────────── */

function Pagina({ children }: { children: React.ReactNode }) {
  return (
    <div data-pantalla="meses">
      <style>{CSS_MESES}</style>
      {children}
    </div>
  )
}

/**
 * La voz pequeña de la pantalla, tomada del inicio: versalitas para los
 * rótulos, monoespaciada para los números de sección, filete en vez de tarjeta
 * para separar. Se declara aquí y no en `tokens.css` porque es composición de
 * esta pantalla, no ficha del tema; los colores, en cambio, salen todos de las
 * fichas y por eso el bloque no tiene un solo hex.
 */
const CSS_MESES = `
[data-pantalla='meses'] .mm-titular {
  font-family: var(--font-display);
  /* El tope deja que un compromiso mensual de seis cifras siga cabiendo en una
     sola línea de 375 px. */
  font-size: clamp(2.2rem, 12vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.055em;
  line-height: 0.88;
  color: var(--ink);
}
[data-pantalla='meses'] .mm-centavos {
  font-size: 0.36em;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink-faint);
}
[data-pantalla='meses'] .mm-versalita {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  line-height: 1.4;
}
[data-pantalla='meses'] .mm-mono {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
[data-pantalla='meses'] .mm-seccion {
  border-top: 1px solid var(--border);
  padding-top: 1.35rem;
  margin-top: 1.5rem;
}
[data-pantalla='meses'] .mm-filas > li + li { border-top: 1px solid var(--border); }
`

/**
 * El encabezado común, en lavanda.
 *
 * Era una copia a mano del de las demás pantallas y se quedó atrás cuando ese
 * pasó a llevar el tinte de su sección: Tarjetas en lavanda y, al entrar a sus
 * compras a meses, un encabezado claro, como si fuera otra parte de la app.
 * Las compras a meses viven en la tarjeta, así que llevan su color.
 */
function Encabezado({ subtitulo, onAnadir }: { subtitulo: string; onAnadir: () => void }) {
  return (
    <ScreenHeader
      title="Compras a meses"
      subtitle={subtitulo || undefined}
      tinte="lavender"
      action={
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onAnadir()
          }}
          aria-label="Registrar una compra a meses"
          className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill bg-accion px-3.5 text-[14px] font-semibold text-on-accion active:scale-95"
        >
          <PlusIcon size={16} weight="bold" />
          Añadir
        </button>
      }
    />
  )
}

function Seccion({
  numero,
  titulo,
  children,
}: {
  numero: string
  titulo: string
  children: React.ReactNode
}) {
  return (
    <section className="mm-seccion">
      <header className="mb-3.5 flex items-baseline gap-2.5">
        <span className="mm-mono shrink-0">{numero}</span>
        <h2 className="min-w-0 flex-1 truncate font-display text-[20px] font-bold tracking-[-0.035em] text-ink">
          {titulo}
        </h2>
      </header>
      {children}
    </section>
  )
}

function Pildora({
  activa,
  icono,
  onClick,
  children,
}: {
  activa: boolean
  icono?: React.ReactNode
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('selection')
        onClick()
      }}
      aria-pressed={activa}
      className={cn(
        'flex min-h-[44px] items-center gap-1.5 rounded-pill px-3.5 text-[13px] font-semibold transition-colors',
        activa
          ? 'bg-ink text-bg'
          : 'bg-transparent text-ink-soft ring-1 ring-inset ring-[var(--border)]',
      )}
    >
      {icono}
      {children}
    </button>
  )
}

function Cargando() {
  return (
    <Pagina>
      <Encabezado subtitulo="Cargando…" onAnadir={() => undefined} />
      <div className="px-safe mx-auto max-w-lg">
        <div className="pb-6 pt-3">
          <Skeleton className="h-3 w-56" />
          <Skeleton className="mt-3 h-11 w-52" />
          <Skeleton className="mt-3 h-3.5 w-full" />
        </div>
        <div className="border-t border-line pt-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 py-3">
              <Skeleton className="size-12 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton className="h-[14px] w-40" />
                <Skeleton className="h-[12px] w-28" />
              </div>
              <Skeleton className="h-[14px] w-16 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </Pagina>
  )
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * Las fechas llegan como `AAAA-MM-DD` y se parten a mano, nunca con `new Date`:
 * construir un `Date` con esa cadena la interpreta en UTC y en México la corre
 * un día hacia atrás. Un corte del 5 se anunciaría el 4.
 */
function formatoCorto(fecha: string): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} ${MESES_CORTOS[Number(mes) - 1]}`
}

function formatoLargo(fecha: string): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} de ${MESES_LARGOS[Number(mes) - 1]}`
}

/**
 * Parte el monto en pesos y centavos, como el titular del inicio.
 *
 * La expresión toma el separador decimal que haya puesto `Intl` en vez de
 * asumir el punto: el día que la app soporte otra moneda, el titular no se
 * parte por la mitad.
 */
function partirMonto(cents: number): { entero: string; centavos: string } {
  const texto = formatMoney(cents)
  const partes = /^(.*)([.,])(\d{2})$/.exec(texto)
  if (!partes) return { entero: texto, centavos: '' }
  return { entero: partes[1]!, centavos: `${partes[2]}${partes[3]}` }
}

function mesYAno(fecha: string): string {
  const [ano, mes] = fecha.split('-')
  return `${MESES_CORTOS[Number(mes) - 1]} ${ano}`
}
